import { ScopeType, spaceMrnFromId } from "~/hooks/useScope";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";

const policyMrnRegex =
  /\/\/policy.api.mondoo.app(?:\/spaces\/(.*)){0,1}\/policies\/(.*)/;

// //policy.api.mondoo.app/policies/amazon-linux-2-level-2 -> amazon-linux-2-level-2
// //policy.api.mondoo.app/spaces/space1/policies/amazon-linux-2-level-2 -> space1/amazon-linux-2-level-2
export const PolicyMrnToId = (mrn: string): string => {
  let m = policyMrnRegex.exec(mrn);
  if (m == null || m.length != 3) {
    return mrn;
  }
  let id = "";
  if (m[1] != null && m[1] != "") {
    id = m[1] + "/";
  }
  return id + m[2];
};

// Determines if a policy is custom based on its MRN
// Custom policies have a space ID in their MRN:
// //policy.api.mondoo.app/spaces/enterprise-demo-edge/policies/test-policy -> true (custom)
// //policy.api.mondoo.app/policies/cis-google-cloud-platform-level-2 -> false (not custom)
export const isPolicyCustom = (mrn: string): boolean => {
  const m = policyMrnRegex.exec(mrn);
  // If regex matches and capture group 1 (space ID) exists and is not empty, it's a custom policy
  return m != null && m.length === 3 && m[1] != null && m[1] !== "";
};

export const PolicyMrnToURIEncodedId = (mrn: string): string => {
  return encodeURIComponent(PolicyMrnToId(mrn));
};

// amazon-linux-2-level-2 -> //policy.api.mondoo.app/policies/amazon-linux-2-level-2
// space1/amazon-linux-2-level-2 -> //policy.api.mondoo.app/spaces/space1/policies/amazon-linux-2-level-2
export const PolicyIdToMrn = (id: string): string => {
  let decoded = decodeURIComponent(id);
  let res = decoded.split("/");
  if (res.length == 1) {
    return `//policy.api.mondoo.app/policies/${res[0]}`;
  }
  if (res.length == 2) {
    return `//policy.api.mondoo.app/spaces/${res[0]}/policies/${res[1]}`;
  } else {
    return id;
  }
};

const spaceIdRegex = /api.mondoo.app(?:\/spaces\/([^\/]+)){0,1}\//;

// TODO: this will work as long as space is the first argument
export const SpaceID = (mrn: string): string => {
  let m = spaceIdRegex.exec(mrn);
  if (m != null && m.length == 2) {
    return m[1];
  }
  return "";
};

export const PLATFORM_POLICY_MRN = "//platform.api.mondoo.app";

// => "//assets.api.mondoo.app/spaces/test-infallible-taussig-796596/assets/2U59H3PHXhC30nTMZ3F0GHimYo4"
// <= "/space/inventory/2U59H3PHXhC30nTMZ3F0GHimYo4?spaceId=test-infallible-taussig-796596"
export function assetMrnToInventoryHref(mrn: string) {
  const rx =
    /^\/\/assets\.api\.mondoo\.app\/spaces\/([\w\d-]+)\/assets\/([\w\d-]+)$/;
  const [validMrn, spaceId, assetId] = rx.exec(mrn) || [];
  if (!validMrn) {
    return null;
  }
  return `/space/inventory/${assetId}?spaceId=${spaceId}`;
}

// Regex pattern for validating workspace MRNs
// https://github.com/mondoohq/server/blob/0c26ceadd842faabf7ef9bfd9ebc562f6f8cf9e6/nexus/mrn/verify.go#L107
// Matches: //policy.api.mondoo.app/spaces/[space-id]/workspaces/[workspace-id]
// Or: //policy.api.mondoo.app/workspaces/[workspace-id] (without spaces)
const VALID_WORKSPACE_MRN_REGEX =
  /^(\/\/policy\.api\.mondoo\.app\/(?:spaces\/[\w\d-]+\/){0,1}workspaces\/[\w\d-]+)$/;

// Regex pattern for validating space MRNs
// Matches: //captain.api.mondoo.app/spaces/[space-id]
const VALID_SPACE_MRN_REGEX = /^\/\/captain\.api\.mondoo\.app\/spaces\/.*$/;

// Regex pattern for validating organization MRNs
// Matches: //captain.api.mondoo.app/spaces/[space-id]
const VALID_ORGANIZATION_MRN_REGEX =
  /^\/\/captain\.api\.mondoo\.app\/organizations\/.*$/;

/**
 * Determines if a string is a valid workspace MRN using regex validation
 * @param mrn - The MRN string to check
 * @returns true if the string is a valid workspace MRN, false otherwise
 */
export function isWorkspaceMrn(mrn: string): boolean {
  if (!mrn) {
    return false;
  }

  return VALID_WORKSPACE_MRN_REGEX.test(mrn);
}

/**
 * Determines if a string is a valid space MRN using regex validation
 * @param mrn - The MRN string to check
 * @returns true if the string is a valid space MRN, false otherwise
 */
export function isSpaceMrn(mrn: string): boolean {
  if (!mrn) {
    return false;
  }

  return VALID_SPACE_MRN_REGEX.test(mrn);
}

/**
 * Determines if a string is a valid organization MRN using regex validation
 * @param mrn - The MRN string to check
 * @returns true if the string is a valid organization MRN, false otherwise
 */
export function isOrganizationMrn(mrn: string): boolean {
  if (!mrn) {
    return false;
  }

  return VALID_ORGANIZATION_MRN_REGEX.test(mrn);
}

/**
 * Determines if a string is the platform MRN
 * @param mrn - The MRN string to check
 * @returns true if the string is the platform MRN, false otherwise
 */
export function isPlatformMrn(mrn: string): boolean {
  if (!mrn) {
    return false;
  }

  return mrn === PLATFORM_POLICY_MRN;
}

export const spaceMrnFromExceptionGroup = (
  exceptionGroup: ExceptionGroup,
): string => {
  // exceptionGroup.scopeMrn is either a spaceMrn or assetMrn
  const { scopeMrn } = exceptionGroup;

  if (isSpaceMrn(scopeMrn)) {
    return scopeMrn;
  }

  if (isAssetMrn(scopeMrn)) {
    return spaceMrnFromAssetMrn(scopeMrn);
  }

  throw new Error(`ExceptionGroup has unexpected scopeMrn: ${scopeMrn}`);
};

export const spaceMrnFromAssetMrn = (assetMrn: string): string => {
  const match = assetMrn.match(VALID_ASSET_MRN_REGEX);
  if (match) {
    const [_validAssetMrn, spaceId] = match;
    return spaceMrnFromId(spaceId);
  }
  throw new Error(`Not valid assetMrn: ${assetMrn}`);
};

// Regex pattern for validating asset MRNs
// Matches: //assets.api.mondoo.app/spaces/[space-id]/assets/[asset-id]
const VALID_ASSET_MRN_REGEX =
  /^\/\/assets\.api\.mondoo\.app\/spaces\/(.*)\/assets\/(.*$)/;

export const isAssetMrn = (mrn: string): boolean => {
  if (!mrn) {
    return false;
  }

  return VALID_ASSET_MRN_REGEX.test(mrn);
};

export function scopeTypeFromMrn(mrn: string): ScopeType {
  switch (true) {
    case isWorkspaceMrn(mrn):
      return ScopeType.Workspace;
    case isSpaceMrn(mrn):
      return ScopeType.Space;
    case isOrganizationMrn(mrn):
      return ScopeType.Organization;
    case isPlatformMrn(mrn):
      return ScopeType.Region;
    default:
      throw new Error("Invalid scopeMrn");
  }
}
